import { useState, useEffect } from "react";
import { Grid2, Select, MenuItem, Box, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import StatusSummaryCards from '../components/technician/TechnicianStatus';
import ProgressPage from '../components/technician/Progress'
import { useGlobalState } from "../contexts/GlobalState";
import { useCommonStyles } from '../styles/GenericStyle';
import LandingPageActiveSPLot from '../components/technician/LandingPageActiveSPLot';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { GenericConstant } from '../utils/GenericConstant';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useSnackbar } from '../contexts/SnackbarContext';

type TechnicianLocationState = {
    openScanner?: boolean;
    openCard?: { type: string; pageTitle: string };
    successSnackbar?: { message?: string; severity?: 'success' | 'info' | 'warning' | 'error'; duration?: number };
};

export const TechnicianView = () => {
    //const classes = useCommonStyles();

    const [isShowScanner, setIsShowScanner] = useState(false);
    const { crops, state: { mobileHome }, setHomePage } = useGlobalState();
    const [cropSelected, setCropSelected] = useState<string | null>(null);
    const [cardClick, setCardClick] = useState({
        "isShow": false,
        "type": "",
        "pageTitle": ""
    });
    const location = useLocation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const classes = useCommonStyles();
    const { menuPosition, calculatePosition } = useDropdownPosition(300);

    const handheldTheme = createTheme({
        typography: {
            body1:    { fontSize: '1.3rem' },
            body2:    { fontSize: '1.1375rem' },
            caption:  { fontSize: '0.975rem' },
            subtitle1:{ fontSize: '1.3rem' },
            subtitle2:{ fontSize: '1.1375rem' },
            overline: { fontSize: '0.975rem' },
            h6:       { fontSize: '1.625rem' },
            h5:       { fontSize: '1.95rem' },
        },
    });
    useEffect(() => {
        mobileHome && setCardClick((prev: any) => ({ ...prev, isShow: false }));
        setHomePage(false);
    }, [mobileHome]);

    useEffect(() => {
        const locationState = location.state as TechnicianLocationState | null;

        if (locationState?.openScanner) {
            setCardClick({ isShow: false, type: '', pageTitle: '' });
            setIsShowScanner(true);
            return;
        }

        if (locationState?.openCard?.type) {
            setIsShowScanner(false);
            setCardClick({
                isShow: true,
                type: locationState.openCard.type,
                pageTitle: locationState.openCard.pageTitle
            });
        }
    }, [location.state]);

    useEffect(() => {
        const locationState = location.state as TechnicianLocationState | null;
        const successMessage = locationState?.successSnackbar?.message?.trim();
        if (!successMessage) return;

        showSnackbar({
            message: successMessage,
            severity: locationState?.successSnackbar?.severity || 'success',
            duration: locationState?.successSnackbar?.duration ?? 4500,
        });

        const { successSnackbar, ...restState } = locationState || {};
        navigate(location.pathname, {
            replace: true,
            state: Object.keys(restState).length > 0 ? restState : null
        });
    }, [location.pathname, location.state, navigate, showSnackbar]);

    const handleItemClick = (item: string) => {

        if (cropSelected == item) {
            setCropSelected("")
        }
    }

    return (

        <ThemeProvider theme={handheldTheme}>
        <Box data-testid="TechnicianView" >
            {(!isShowScanner && !cardClick.isShow) && <Grid2 size={{ xs: 12, sm: 12, md: 5 }} sx={{ px: 2 }}>
                <Select displayEmpty IconComponent={KeyboardArrowDownOutlinedIcon} sx={{
                    width: '100%',
                    height: 40,
                    borderRadius: '4px',
                    opacity: 1,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #8D8D8D',
                    mt: '27px',
                    mb: '24px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                    }
                }} value={cropSelected ?? ""} onChange={(e) => setCropSelected(e.target.value as string)}
                    onOpen={calculatePosition}
                    MenuProps={{
                        anchorOrigin: menuPosition.anchorOrigin,
                        transformOrigin: menuPosition.transformOrigin,
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                marginTop: menuPosition.anchorOrigin.vertical === "top" ? -8 : 8
                            },
                            sx: {
                                '& .MuiMenuItem-root': {
                                    padding: '10px',                                  
                                    minHeight: '30px',     
                                },
                            },
                        },
                    }}

                    renderValue={(selected) => {

                        return selected ? <Typography component="span" sx={{ ...GenericConstant.DROPDOWNSTYLE, fontSize: '20.8px' }}>
                            {crops.find((ele) => ele?.crop_xrd_oid == selected)?.name}
                        </Typography> : <Typography component="span" className={classes.labelNoto16Bold400_hh} color="#0A0A0A">All Crops</Typography>
                    }}>

                    {crops?.map((crop, index) => (
                        <MenuItem disableRipple sx={{height: 10 }} className={classes.labelNoto16Bold400_hh} color="#0A0A0A" key={'spLotCrops' + index} value={crop?.crop_xrd_oid ?? ""} onClick={() => handleItemClick(crop?.crop_xrd_oid)}>{crop?.name}
                        </MenuItem>
                    ))}
                </Select>

            </Grid2>}
            {(!isShowScanner && !cardClick.isShow) && <StatusSummaryCards crop={cropSelected ?? ""} setCardType={setCardClick} />}
            {!cardClick?.isShow && <ProgressPage setIsShowSummaryCards={setIsShowScanner} />}
            {cardClick.isShow && <LandingPageActiveSPLot
                type={cardClick?.type ?? ""}
                setCardType={setCardClick} crop={cropSelected ?? ""}
                pageTitle={cardClick?.pageTitle ?? ""}
            />}
        </Box>
        </ThemeProvider>
    )
}
export default TechnicianView;